import {useDebouncedCallback, useMediaQuery} from "@mantine/hooks";
import CrossIcon from "@atlaskit/icon/core/cross";
import SearchIcon from "@atlaskit/icon/core/search";
import {useCallback, useEffect, useId, useRef, useState, type ChangeEvent} from "react";
import type {OttopilotDataViewerController} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {AnankeIconButton} from "@vesta/ananke/components/anankeIconButton";
import {AnankeTextField} from "@vesta/ananke/components/anankeTextField";
import {generateUuid} from "@vesta/global-common-typescript/utilities/utilities";
import {twJoin} from "tailwind-merge";

const MOBILE_BREAKPOINT = "(max-width: 47.9375em)";

export function SearchComponent<
    T extends {
        id: Uuid;
    },
>({
    controller,
    placeholder = "Search",
    onMobileSearchExpandedChange,
}: {
    controller: OttopilotDataViewerController<T>;
    placeholder?: string;
    /** Fires when the mobile expanded search panel opens or closes (desktop always reports `false`). */
    onMobileSearchExpandedChange?: (expanded: boolean) => void;
}) {
    const [searchValue, setSearchValue] = useState<string>(controller.searchConfiguration.searchValue);
    const lastCommittedSearchRef = useRef<string>(controller.searchConfiguration.searchValue);
    const queryKeyPrefixKey = JSON.stringify(controller.queryKeyPrefix);
    const isMobileViewport = useMediaQuery(MOBILE_BREAKPOINT, false, {getInitialValueInEffect: true});
    const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
    const reactId = useId();
    const searchInputId = `ottopilot-search-${reactId.replace(/:/g, "")}`;

    const handleSearch = useDebouncedCallback((query: string) => {
        lastCommittedSearchRef.current = query;
        controller.setSearchConfiguration((prev) => ({
            ...prev,
            revisionId: generateUuid(),
            searchValue: query,
        }));
    }, 500);

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        setSearchValue(event.currentTarget.value);
        handleSearch(event.currentTarget.value);
    };

    const clearSearch = useCallback(() => {
        lastCommittedSearchRef.current = "";
        setSearchValue("");
        controller.setSearchConfiguration((prev) => ({
            ...prev,
            revisionId: generateUuid(),
            searchValue: "",
        }));
    }, [controller]);

    const closeMobileSearch = useCallback(() => {
        setIsMobileSearchOpen(false);
    }, []);

    useEffect(() => {
        if (isMobileViewport === false) {
            setIsMobileSearchOpen(false);
        }
    }, [isMobileViewport]);

    useEffect(() => {
        if (onMobileSearchExpandedChange === undefined) {
            return;
        }
        if (isMobileViewport === false) {
            onMobileSearchExpandedChange(false);
            return;
        }
        onMobileSearchExpandedChange(isMobileSearchOpen);
    }, [isMobileViewport, isMobileSearchOpen, onMobileSearchExpandedChange]);

    useEffect(() => {
        if (isMobileViewport === false || isMobileSearchOpen === false) {
            return;
        }
        const timeoutId = window.setTimeout(() => {
            const el = document.getElementById(searchInputId);
            if (el !== null && el instanceof HTMLInputElement) {
                el.focus();
            }
        }, 50);
        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [isMobileViewport, isMobileSearchOpen, searchInputId]);

    useEffect(() => {
        if (isMobileViewport === false || isMobileSearchOpen === false) {
            return;
        }
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                closeMobileSearch();
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => {
            window.removeEventListener("keydown", onKeyDown);
        };
    }, [isMobileViewport, isMobileSearchOpen, closeMobileSearch]);

    useEffect(() => {
        const fromController = controller.searchConfiguration.searchValue;
        setSearchValue(fromController);
        lastCommittedSearchRef.current = fromController;
    }, [queryKeyPrefixKey]);

    useEffect(() => {
        const fromController = controller.searchConfiguration.searchValue;
        if (fromController === lastCommittedSearchRef.current) {
            return;
        }
        if (searchValue !== lastCommittedSearchRef.current) {
            return;
        }
        setSearchValue(fromController);
        lastCommittedSearchRef.current = fromController;
    }, [controller.searchConfiguration.searchValue, controller.searchConfiguration.revisionId, searchValue]);

    const clearButton =
        searchValue.length > 0 ? (
            <div className="mr-ds-space-100 grid place-items-center bg-black rounded-full">
                <AnankeIconButton
                    type="button"
                    appearance="subtle"
                    shape="circle"
                    spacing="compact"
                    icon={(iconProp) => <CrossIcon {...iconProp} color={"white"} size={"small"}/>}
                    label="Clear search"
                    onClick={() => {
                        clearSearch();
                    }}
                />
            </div>
        ) : null;

    const searchField = (
        <AnankeTextField
            id={searchInputId}
            value={searchValue}
            onChange={handleChange}
            placeholder={placeholder}
            elemBeforeInput={
                <div className="pl-ds-space-100 grid place-items-center text-ds-text">
                    <SearchIcon label="" />
                </div>
            }
            elemAfterInput={clearButton}
        />
    );

    if (isMobileViewport === false) {
        return searchField;
    }

    if (isMobileSearchOpen === false) {
        return (
            <div className="w-full transition-all duration-300 ease-out">
                <AnankeIconButton
                    type="button"
                    appearance="default"
                    icon={(iconProps) => (
                        <SearchIcon
                            {...iconProps}
                            label=""
                            size="small"
                        />
                    )}
                    label="Open search"
                    aria-expanded={false}
                    className="p-ds-space-100"
                    onClick={() => {
                        setIsMobileSearchOpen(true);
                    }}
                />
            </div>
        );
    }

    return (
        <div
            className={twJoin(
                "grid w-full min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-ds-space-100",
                "animate-in fade-in slide-in-from-left-2 duration-300 ease-out",
            )}
        >
            <div className="min-w-0 transition-[opacity,transform] duration-300 ease-out">{searchField}</div>
            <AnankeIconButton
                type="button"
                appearance="subtle"
                icon={CrossIcon}
                label="Close search"
                onClick={() => {
                    closeMobileSearch();
                }}
            />
        </div>
    );
}
